import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import axios from 'axios';
import Header from '../Header';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  ShieldAlert, GitBranch, Plus, Play, Loader2, Trash2, FileCode2, KeyRound,
  Boxes, Server, RefreshCw, ExternalLink, Search, ChevronRight, X, Sparkles,
  Target, Wrench, Layers, ChevronDown, Download,
} from 'lucide-react';

const API = '/api/vapt';

const SEV_META = {
  critical: { label: 'Critical', cls: 'bg-red-600 text-white', dot: 'bg-red-600' },
  high: { label: 'High', cls: 'bg-orange-500 text-white', dot: 'bg-orange-500' },
  medium: { label: 'Medium', cls: 'bg-amber-400 text-black', dot: 'bg-amber-400' },
  low: { label: 'Low', cls: 'bg-sky-500 text-white', dot: 'bg-sky-500' },
  informational: { label: 'Info', cls: 'bg-slate-400 text-white', dot: 'bg-slate-400' },
};

const CAT_META = {
  sca: { label: 'Dependency', icon: Boxes },
  sast: { label: 'Code', icon: FileCode2 },
  secret: { label: 'Secret', icon: KeyRound },
  iac: { label: 'Config', icon: Server },
};

const SevBadge = ({ sev }) => {
  const m = SEV_META[sev] || SEV_META.medium;
  return <Badge className={`${m.cls} border-none text-[10px] font-semibold uppercase tracking-wide px-2`}>{m.label}</Badge>;
};

const SevDist = ({ counts }) => (
  <div className="flex items-center gap-1.5">
    {['critical', 'high', 'medium', 'low', 'informational'].map((s) =>
      counts[s] ? (
        <span key={s} className="inline-flex items-center gap-1 text-[10px] text-black/55 dark:text-white/55">
          <span className={`w-1.5 h-1.5 rounded-full ${SEV_META[s].dot}`} />{counts[s]}
        </span>
      ) : null
    )}
  </div>
);

const GroupCard = ({ group, expanded, onToggle, onOpenFinding }) => {
  const Cat = (CAT_META[group.kind] || {}).icon || FileCode2;
  return (
    <Card className={`border transition-colors ${group.low_confidence
      ? 'border-black/8 dark:border-white/8 bg-black/[0.015] dark:bg-white/[0.02]'
      : 'border-black/10 dark:border-white/10 hover:border-[#0F62FE]/40'}`}
      data-testid={`vapt-group-${group.key}`}>
      <button type="button" onClick={onToggle} data-testid={`vapt-group-toggle-${group.key}`}
        className="w-full text-left p-3.5 flex items-start gap-3">
        <SevBadge sev={group.severity} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Cat className="w-3.5 h-3.5 text-black/40 dark:text-white/40 shrink-0" />
            <span className="text-sm font-medium text-[#0A0A0A] dark:text-[#F5F5F5] truncate font-mono">{group.label}</span>
            <span className="text-[11px] text-black/45 dark:text-white/45">· {group.count} finding{group.count !== 1 ? 's' : ''}</span>
            {group.cve_count > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">{group.cve_count} CVE{group.cve_count !== 1 ? 's' : ''}</span>
            )}
            {group.fix_available && group.fixed_versions.length > 0 && (
              <span title={`Upgrade fixes: ${group.fixed_versions.join(', ')}`}
                className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0] shrink-0">
                <Wrench className="w-2.5 h-2.5" /> Upgrade → {group.fixed_versions.slice(0, 2).join(', ')}{group.fixed_versions.length > 2 ? '…' : ''}
              </span>
            )}
            {group.low_confidence && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] shrink-0">low-confidence · verify</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
            <SevDist counts={group.sev_counts} />
            {group.versions.length > 0 && (
              <span className="text-[10px] text-black/45 dark:text-white/45 font-mono truncate max-w-[240px]">v{group.versions.join(', v')}</span>
            )}
            {(group.frameworks || []).slice(0, 3).map((fw) => (
              <Badge key={fw} variant="outline" className="text-[9px] px-1.5 py-0 border-black/15 dark:border-white/15">{fw}</Badge>
            ))}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-black/25 dark:text-white/25 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="border-t border-black/8 dark:border-white/8 divide-y divide-black/5 dark:divide-white/5" data-testid={`vapt-group-items-${group.key}`}>
          {group.findings.map((f) => (
            <button key={f.id} onClick={() => onOpenFinding(f.id)} data-testid={`vapt-group-finding-${f.id}`}
              className="w-full text-left px-4 py-2.5 hover:bg-black/[0.03] dark:hover:bg-white/[0.05] flex items-start gap-3">
              <SevBadge sev={f.severity} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-[#0A0A0A] dark:text-[#F5F5F5] truncate">{f.title}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[10px] text-black/45 dark:text-white/45">
                  {f.version && <span className="font-mono">v{f.version}{f.fixed_version ? ` → ${f.fixed_version}` : ''}</span>}
                  {f.location?.file && <span className="font-mono truncate max-w-[260px]">{f.location.file}{f.location.line ? `:${f.location.line}` : ''}</span>}
                  {(f.cve_ids || []).slice(0, 2).map((cve) => <span key={cve} className="text-red-600">{cve}</span>)}
                </div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-black/20 dark:text-white/20 shrink-0 mt-0.5" />
            </button>
          ))}
        </div>
      )}
    </Card>
  );
};

const VaptDashboard = () => {
  const { currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [assets, setAssets] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [findings, setFindings] = useState([]);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [findingsMeta, setFindingsMeta] = useState({ total: 0, matched: 0, returned: 0 });
  const [sevFilter, setSevFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [fixFilter, setFixFilter] = useState('all');
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [groupBy, setGroupBy] = useState('component');
  const [groups, setGroups] = useState([]);
  const [groupsMeta, setGroupsMeta] = useState({ total: 0, matched: 0, group_count: 0, low_confidence_groups: 0 });
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showTriage, setShowTriage] = useState(false);
  const [activeFinding, setActiveFinding] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Add-repo dialog
  const [addOpen, setAddOpen] = useState(false);
  const [repos, setRepos] = useState([]);
  const [reposConnected, setReposConnected] = useState(true);
  const [reposLoading, setReposLoading] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');

  const pollRef = useRef(null);

  const loadCore = useCallback(async () => {
    try {
      const [s, a, sc] = await Promise.all([
        axios.get(`${API}/summary`),
        axios.get(`${API}/assets`),
        axios.get(`${API}/scans`),
      ]);
      setSummary(s.data);
      setAssets(a.data.assets || []);
      setScans(sc.data.scans || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('VAPT load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCore(); }, [loadCore, currentWorkspace]);

  // Poll while any scan is running
  const anyRunning = scans.some((s) => ['queued', 'running'].includes(s.status));
  useEffect(() => {
    if (anyRunning) {
      pollRef.current = setInterval(loadCore, 4000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
  }, [anyRunning, loadCore]);

  const scanForAsset = (assetId) =>
    scans.find((s) => s.asset_id === assetId) || null;

  const openRepos = async () => {
    setAddOpen(true);
    setReposLoading(true);
    try {
      const r = await axios.get(`${API}/github/repos`);
      setReposConnected(r.data.connected);
      setRepos(r.data.repos || []);
    } catch (e) {
      toast.error('Could not load repositories');
    } finally {
      setReposLoading(false);
    }
  };

  const addAsset = async (repo) => {
    try {
      await axios.post(`${API}/assets`, {
        repo_full_name: repo.full_name,
        repo_url: repo.html_url,
        default_branch: repo.default_branch,
        private: repo.private,
      });
      toast.success(`Added ${repo.full_name}`);
      setRepos((prev) => prev.map((r) => r.full_name === repo.full_name ? { ...r, already_added: true } : r));
      loadCore();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add repository');
    }
  };

  const runScan = async (asset) => {
    try {
      await axios.post(`${API}/assets/${asset.id}/scan`, {});
      toast.success(`Scan started for ${asset.repo_full_name}`);
      loadCore();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start scan');
    }
  };

  const removeAsset = async (asset) => {
    if (!window.confirm(`Remove ${asset.repo_full_name} and its scan results?`)) return;
    try {
      await axios.delete(`${API}/assets/${asset.id}`);
      toast.success('Repository removed');
      if (selectedAsset?.id === asset.id) { setSelectedAsset(null); setFindings([]); }
      loadCore();
    } catch (e) {
      toast.error('Failed to remove repository');
    }
  };

  const fetchFindings = useCallback(async (asset, opts) => {
    if (!asset) return;
    const o = opts || {};
    const pr = o.priorityOnly !== undefined ? o.priorityOnly : priorityOnly;
    const sev = o.sevFilter !== undefined ? o.sevFilter : sevFilter;
    const cat = o.catFilter !== undefined ? o.catFilter : catFilter;
    const fix = o.fixFilter !== undefined ? o.fixFilter : fixFilter;
    const params = { asset_id: asset.id };
    if (pr) params.priority = true;
    if (sev !== 'all') params.severity = sev;
    if (cat !== 'all') params.category = cat;
    if (fix === 'has') params.fix_available = true;
    if (fix === 'no') params.fix_available = false;
    setFindingsLoading(true);
    try {
      const r = await axios.get(`${API}/findings`, { params });
      setFindings(r.data.findings || []);
      setFindingsMeta({
        total: r.data.total || 0,
        matched: r.data.matched ?? (r.data.findings || []).length,
        returned: r.data.returned ?? (r.data.findings || []).length,
      });
    } catch (e) {
      toast.error('Could not load findings');
    } finally {
      setFindingsLoading(false);
    }
  }, [API, priorityOnly, sevFilter, catFilter, fixFilter]);

  const viewFindings = (asset) => {
    setSelectedAsset(asset);
    setFindings([]);
    setGroups([]);
    if (groupBy === 'flat') fetchFindings(asset);
    else fetchGroups(asset);
  };

  const fetchGroups = useCallback(async (asset) => {
    if (!asset || groupBy === 'flat') return;
    const params = { asset_id: asset.id, group_by: groupBy };
    if (priorityOnly) params.priority = true;
    if (sevFilter !== 'all') params.severity = sevFilter;
    if (catFilter !== 'all') params.category = catFilter;
    if (fixFilter === 'has') params.fix_available = true;
    if (fixFilter === 'no') params.fix_available = false;
    setGroupsLoading(true);
    try {
      const r = await axios.get(`${API}/findings/grouped`, { params });
      setGroups(r.data.groups || []);
      setGroupsMeta({
        total: r.data.total || 0,
        matched: r.data.matched || 0,
        group_count: r.data.group_count || 0,
        low_confidence_groups: r.data.low_confidence_groups || 0,
      });
      setExpandedGroups({});
    } catch (e) {
      toast.error('Could not load grouped findings');
    } finally {
      setGroupsLoading(false);
    }
  }, [groupBy, priorityOnly, sevFilter, catFilter, fixFilter]);

  const openFindingById = async (id) => {
    try {
      const r = await axios.get(`${API}/findings/${id}`);
      setActiveFinding(r.data.finding);
    } catch (e) {
      toast.error('Could not load finding');
    }
  };

  const downloadExcel = async (asset) => {
    if (!asset) return;
    setExporting(true);
    try {
      const r = await axios.get(`${API}/findings/export`, {
        params: { asset_id: asset.id },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'vapt_findings.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Findings exported to Excel');
    } catch (e) {
      toast.error('Could not export findings');
    } finally {
      setExporting(false);
    }
  };

  // Re-query the server whenever a filter changes (filtering + severity sort
  // run across ALL findings server-side, so the page cap never hides a
  // higher-priority finding on large repos).
  useEffect(() => {
    if (!selectedAsset) return;
    if (groupBy === 'flat') fetchFindings(selectedAsset);
    else fetchGroups(selectedAsset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityOnly, sevFilter, catFilter, fixFilter, groupBy]);

  const filteredFindings = findings;
  const primaryGroups = groups.filter((g) => !g.low_confidence);
  const triageGroups = groups.filter((g) => g.low_confidence);

  const filteredRepos = repos.filter((r) =>
    r.full_name?.toLowerCase().includes(repoSearch.toLowerCase()));

  const c = summary?.counts || {};

  return (
    <div className="min-h-screen bg-appbg dark:bg-[#050505]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="vapt-dashboard">
        {/* Title */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-[#0F172A] dark:bg-[#101F18] flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-display text-[#0A0A0A] dark:text-[#F5F5F5]">VAPT</h1>
            </div>
            <p className="text-sm text-black/55 dark:text-white/55 mt-2 max-w-2xl">
              AI-assisted source-code vulnerability scanning across your connected GitHub repositories —
              dependencies, code, secrets and infrastructure, mapped to your compliance frameworks.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={loadCore} data-testid="vapt-refresh-btn"
              className="border-black/15 dark:border-white/15">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={openRepos} data-testid="vapt-add-repo-btn"
              className="bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC]">
              <Plus className="w-4 h-4 mr-1.5" /> Add Repository
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="vapt-summary">
          {['critical', 'high', 'medium', 'low', 'informational'].map((sev) => (
            <Card key={sev} className="border-black/10 dark:border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${SEV_META[sev].dot}`} />
                  <span className="text-[11px] font-label uppercase tracking-wide text-black/55 dark:text-white/55">{SEV_META[sev].label}</span>
                </div>
                <div className="text-2xl font-display text-[#0A0A0A] dark:text-[#F5F5F5] mt-1" data-testid={`vapt-count-${sev}`}>
                  {c[sev] ?? 0}
                </div>
              </CardContent>
            </Card>
          ))}
          <Card className="border-black/10 dark:border-white/10 bg-[#D4E0DA]/30 dark:bg-[#101F18]/30">
            <CardContent className="p-4">
              <span className="text-[11px] font-label uppercase tracking-wide text-black/55 dark:text-white/55">Repos</span>
              <div className="text-2xl font-display text-[#0A0A0A] dark:text-[#F5F5F5] mt-1">{summary?.assets ?? 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Repositories */}
        <div className="mb-8">
          <h2 className="text-lg font-display text-[#0A0A0A] dark:text-[#F5F5F5] mb-3 flex items-center gap-2">
            <GitBranch className="w-4 h-4" /> Repositories
          </h2>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-black/50 dark:text-white/50 py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : assets.length === 0 ? (
            <Card className="border-dashed border-black/15 dark:border-white/15">
              <CardContent className="p-8 text-center">
                <GitBranch className="w-8 h-8 mx-auto text-black/25 dark:text-white/25 mb-3" />
                <p className="text-sm text-black/60 dark:text-white/60 mb-4">No repositories added yet. Add one from your connected GitHub account to run your first scan.</p>
                <Button onClick={openRepos} data-testid="vapt-empty-add-btn"
                  className="bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC]">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Repository
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="vapt-asset-list">
              {assets.map((asset) => {
                const scan = scanForAsset(asset.id);
                const running = scan && ['queued', 'running'].includes(scan.status);
                const counts = scan?.summary?.counts;
                return (
                  <Card key={asset.id} className="border-black/10 dark:border-white/10" data-testid={`vapt-asset-${asset.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-[#0A0A0A] dark:text-[#F5F5F5] truncate">{asset.repo_full_name}</span>
                            {asset.private && <Badge variant="outline" className="text-[9px] px-1.5 border-black/20 dark:border-white/20">private</Badge>}
                          </div>
                          <a href={asset.repo_url} target="_blank" rel="noreferrer"
                            className="text-[11px] text-[#0F62FE] dark:text-[#5C97FF] inline-flex items-center gap-1 mt-0.5 hover:underline">
                            View on GitHub <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <button onClick={() => removeAsset(asset)} data-testid={`vapt-remove-${asset.id}`}
                          className="text-black/30 dark:text-white/30 hover:text-red-500 transition-colors p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* status row */}
                      <div className="mt-3 min-h-[24px]">
                        {running ? (
                          <div className="flex items-center gap-2 text-[12px] text-[#0F62FE] dark:text-[#5C97FF]">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {scan?.progress?.message || 'Scanning…'}
                          </div>
                        ) : counts ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            {['critical', 'high', 'medium', 'low'].map((sv) => (
                              counts[sv] ? (
                                <span key={sv} className="inline-flex items-center gap-1 text-[11px]">
                                  <span className={`w-2 h-2 rounded-full ${SEV_META[sv].dot}`} />{counts[sv]}
                                </span>
                              ) : null
                            ))}
                            <span className="text-[11px] text-black/45 dark:text-white/45">· {counts.total || 0} findings</span>
                          </div>
                        ) : scan?.status === 'failed' ? (
                          <span className="text-[12px] text-red-500">Scan failed: {scan?.error}</span>
                        ) : (
                          <span className="text-[12px] text-black/40 dark:text-white/40">Not scanned yet</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mt-3">
                        <Button size="sm" disabled={running} onClick={() => runScan(asset)}
                          data-testid={`vapt-scan-${asset.id}`}
                          className="bg-[#0A0A0A] dark:bg-[#141414] text-white hover:bg-[#0F62FE] disabled:opacity-50">
                          {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                          {running ? 'Scanning' : 'Scan'}
                        </Button>
                        {counts && (
                          <Button size="sm" variant="outline" onClick={() => viewFindings(asset)}
                            data-testid={`vapt-view-${asset.id}`}
                            className="border-black/15 dark:border-white/15">
                            View Findings <ChevronRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Findings */}
        {selectedAsset && (
          <div data-testid="vapt-findings-panel">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="text-lg font-display text-[#0A0A0A] dark:text-[#F5F5F5] flex items-center gap-2">
                Findings · <span className="text-black/55 dark:text-white/55 text-base">{selectedAsset.repo_full_name}</span>
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadExcel(selectedAsset)}
                  disabled={exporting}
                  data-testid="vapt-export-excel"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[13px] font-medium border border-black/15 dark:border-white/15 text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50 transition-colors"
                  title="Download all findings for this repository as an Excel file"
                >
                  {exporting
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Download className="w-3.5 h-3.5" />}
                  Export Excel
                </button>
                <button onClick={() => { setSelectedAsset(null); setFindings([]); }}
                  className="text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Scanner warnings (timeouts / partial results) */}
            {(() => {
              const scan = scanForAsset(selectedAsset.id);
              const warns = scan?.summary?.warnings || [];
              return warns.length ? (
                <Card className="border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 mb-4" data-testid="vapt-warnings">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <ShieldAlert className="w-4 h-4 text-amber-600" />
                      <span className="text-[11px] font-label uppercase tracking-wide text-amber-700 dark:text-amber-400">Partial results — some scanners did not finish</span>
                    </div>
                    <ul className="text-xs text-amber-800 dark:text-amber-300 list-disc pl-5 space-y-0.5">
                      {warns.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                    <p className="text-[11px] text-amber-700/80 dark:text-amber-400/70 mt-1.5">
                      Findings for the affected categories may be incomplete for this large repository.
                    </p>
                  </CardContent>
                </Card>
              ) : null;
            })()}

            {/* AI summary */}
            {(() => {
              const scan = scanForAsset(selectedAsset.id);
              const ai = scan?.summary?.ai_summary;
              return ai ? (
                <Card className="border-black/10 dark:border-white/10 bg-[#D9CFD9]/20 dark:bg-[#1E1724]/20 mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Sparkles className="w-4 h-4 text-[#7C3AED]" />
                      <span className="text-[11px] font-label uppercase tracking-wide text-black/55 dark:text-white/55">AI Security Summary</span>
                    </div>
                    <p className="text-sm text-black/75 dark:text-white/75 leading-relaxed">{ai}</p>
                  </CardContent>
                </Card>
              ) : null;
            })()}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-56 h-9 text-sm border-black/15 dark:border-white/15" data-testid="vapt-groupby-filter">
                  <div className="flex items-center gap-1.5 min-w-0"><Layers className="w-3.5 h-3.5 shrink-0" /><SelectValue placeholder="Group by" /></div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">No grouping (flat)</SelectItem>
                  <SelectItem value="component">Group by component</SelectItem>
                  <SelectItem value="secret_file">Group by secret file</SelectItem>
                  <SelectItem value="module">Group by module</SelectItem>
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => setPriorityOnly((v) => !v)}
                data-testid="vapt-priority-toggle"
                role="switch"
                aria-checked={priorityOnly}
                aria-label="Priority only"
                title="Show only the findings worth acting on first: Critical/High severity, and — for vulnerable dependencies — ones that have an available fix."
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border transition-colors ${priorityOnly
                  ? 'bg-[#0F62FE] text-white border-[#0F62FE]'
                  : 'bg-transparent text-black/70 dark:text-white/70 border-black/15 dark:border-white/15 hover:bg-black/[0.03] dark:hover:bg-white/[0.05]'}`}
              >
                <Target className="w-3.5 h-3.5" />
                Priority only
              </button>
              <Select value={sevFilter} onValueChange={setSevFilter}>
                <SelectTrigger className="w-36 h-9 text-sm border-black/15 dark:border-white/15" data-testid="vapt-sev-filter">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All severities</SelectItem>
                  {['critical', 'high', 'medium', 'low', 'informational'].map((s) => (
                    <SelectItem key={s} value={s}>{SEV_META[s].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger className="w-36 h-9 text-sm border-black/15 dark:border-white/15" data-testid="vapt-cat-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {Object.entries(CAT_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={fixFilter} onValueChange={setFixFilter}>
                <SelectTrigger className="w-36 h-9 text-sm border-black/15 dark:border-white/15" data-testid="vapt-fix-filter">
                  <SelectValue placeholder="Fix" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any fix status</SelectItem>
                  <SelectItem value="has">Fix available</SelectItem>
                  <SelectItem value="no">No fix yet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mb-3 text-[12px] text-black/50 dark:text-white/50" data-testid="vapt-findings-count-line">
              {groupBy !== 'flat' ? (
                <>Showing <b className="text-black/70 dark:text-white/70">{groupsMeta.group_count}</b> groups
                {' '}({groupsMeta.matched} finding{groupsMeta.matched !== 1 ? 's' : ''}{groupsMeta.matched !== groupsMeta.total ? ` of ${groupsMeta.total}` : ''})
                {groupsMeta.low_confidence_groups > 0 && <> · {groupsMeta.low_confidence_groups} low-confidence group{groupsMeta.low_confidence_groups !== 1 ? 's' : ''} in triage</>}
                {priorityOnly && <> · low-confidence matches hidden — <button type="button" className="underline hover:no-underline" data-testid="vapt-group-show-lowconf" onClick={() => setPriorityOnly(false)}>show triage</button></>}
                </>
              ) : priorityOnly ? (
                <>Showing <b className="text-black/70 dark:text-white/70">{findingsMeta.matched}</b> priority findings
                {findingsMeta.total > findingsMeta.matched && (
                  <> of {findingsMeta.total} total — <button type="button" className="underline hover:no-underline" data-testid="vapt-show-all" onClick={() => setPriorityOnly(false)}>show all {findingsMeta.total}</button></>
                )}
                {findingsMeta.returned < findingsMeta.matched && <> (top {findingsMeta.returned} shown)</>}
                </>
              ) : (
                <>Showing <b className="text-black/70 dark:text-white/70">{findingsMeta.matched}</b>{findingsMeta.matched !== findingsMeta.total ? ` of ${findingsMeta.total}` : ''} findings
                {findingsMeta.returned < findingsMeta.matched && <> (top {findingsMeta.returned} shown)</>}
                {' · '}<button type="button" className="underline hover:no-underline" data-testid="vapt-priority-link" onClick={() => setPriorityOnly(true)}>focus on priority</button>
                </>
              )}
            </div>

            {groupBy !== 'flat' ? (
              groupsLoading ? (
                <div className="flex items-center gap-2 text-sm text-black/50 dark:text-white/50 py-8">
                  <Loader2 className="w-4 h-4 animate-spin" /> Grouping findings…
                </div>
              ) : groups.length === 0 ? (
                <Card className="border-dashed border-black/15 dark:border-white/15">
                  <CardContent className="p-8 text-center text-sm text-black/55 dark:text-white/55">
                    {groupsMeta.total === 0 ? 'No findings — this repository looks clean 🎉' : 'No findings match this filter.'}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2" data-testid="vapt-groups-list">
                  {primaryGroups.map((g) => (
                    <GroupCard key={g.key} group={g}
                      expanded={!!expandedGroups[g.key]}
                      onToggle={() => setExpandedGroups((p) => ({ ...p, [g.key]: !p[g.key] }))}
                      onOpenFinding={openFindingById} />
                  ))}
                  {triageGroups.length > 0 && (
                    <div className="pt-2" data-testid="vapt-triage-section">
                      <button type="button" onClick={() => setShowTriage((v) => !v)}
                        data-testid="vapt-triage-toggle"
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12px] font-medium text-black/55 dark:text-white/55 bg-black/[0.03] dark:bg-white/[0.04] border border-dashed border-black/12 dark:border-white/12 hover:bg-black/[0.05]">
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showTriage ? 'rotate-180' : ''}`} />
                        Low-confidence · needs triage
                        <span className="ml-1 text-black/40 dark:text-white/40">
                          {triageGroups.length} group{triageGroups.length !== 1 ? 's' : ''} · {triageGroups.reduce((n, g) => n + g.count, 0)} findings
                        </span>
                        <span className="ml-auto text-[10px] text-black/40 dark:text-white/40">generic entropy matches — likely false positives</span>
                      </button>
                      {showTriage && (
                        <div className="space-y-2 mt-2">
                          {triageGroups.map((g) => (
                            <GroupCard key={g.key} group={g}
                              expanded={!!expandedGroups[g.key]}
                              onToggle={() => setExpandedGroups((p) => ({ ...p, [g.key]: !p[g.key] }))}
                              onOpenFinding={openFindingById} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            ) : findingsLoading ? (
              <div className="flex items-center gap-2 text-sm text-black/50 dark:text-white/50 py-8">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading findings…
              </div>
            ) : filteredFindings.length === 0 ? (
              <Card className="border-dashed border-black/15 dark:border-white/15">
                <CardContent className="p-8 text-center text-sm text-black/55 dark:text-white/55">
                  {findingsMeta.total === 0
                    ? 'No findings — this repository looks clean 🎉'
                    : (priorityOnly
                        ? 'No priority findings for this filter. Everything Critical/High here either has no fix yet or is a transitive dependency.'
                        : 'No findings match this filter.')}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="vapt-findings-list">
                {filteredFindings.map((f) => {
                  const Cat = (CAT_META[f.category] || {}).icon || FileCode2;
                  return (
                    <button key={f.id} onClick={() => setActiveFinding(f)}
                      data-testid={`vapt-finding-${f.id}`}
                      className="w-full text-left">
                      <Card className="border-black/10 dark:border-white/10 hover:border-[#0F62FE]/40 transition-colors">
                        <CardContent className="p-3.5">
                          <div className="flex items-start gap-3">
                            <SevBadge sev={f.severity} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <Cat className="w-3.5 h-3.5 text-black/40 dark:text-white/40 shrink-0" />
                                <span className="text-sm font-medium text-[#0A0A0A] dark:text-[#F5F5F5] truncate">{f.title}</span>
                                {f.fix_available && (
                                  <span data-testid={`vapt-fix-badge-${f.id}`} title={f.fixed_version ? `Fix available: upgrade to ${f.fixed_version}` : 'A fix is available'}
                                    className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0] shrink-0">
                                    <Wrench className="w-2.5 h-2.5" /> Fix{f.fixed_version ? ` → ${f.fixed_version}` : ''}
                                  </span>
                                )}
                                {f.dependency_relationship === 'transitive' && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/45 dark:text-white/45 border border-black/10 dark:border-white/10 shrink-0">transitive</span>
                                )}
                                {f.category === 'secret' && f.confidence === 'low' && (
                                  <span data-testid={`vapt-lowconf-${f.id}`} title="Low-confidence match (generic entropy rule) — often a false positive. Verify before acting." className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] shrink-0">low-confidence</span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-black/50 dark:text-white/50">
                                {f.location?.file && (
                                  <span className="font-mono truncate max-w-[280px]">
                                    {f.location.file}{f.location.line ? `:${f.location.line}` : ''}
                                  </span>
                                )}
                                <span className="uppercase tracking-wide">{(CAT_META[f.category] || {}).label || f.category}</span>
                                {(f.frameworks || []).slice(0, 3).map((fw) => (
                                  <Badge key={fw} variant="outline" className="text-[9px] px-1.5 py-0 border-black/15 dark:border-white/15">{fw}</Badge>
                                ))}
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-black/25 dark:text-white/25 shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Repository dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl" data-testid="vapt-add-repo-dialog">
          <DialogHeader>
            <DialogTitle>Add a repository</DialogTitle>
            <DialogDescription>
              Pick a repository from your connected GitHub account to scan.
            </DialogDescription>
          </DialogHeader>
          {!reposConnected ? (
            <div className="py-8 text-center">
              <p className="text-sm text-black/60 dark:text-white/60 mb-4">
                GitHub isn't connected for this workspace yet.
              </p>
              <Button onClick={() => navigate('/technologies')} data-testid="vapt-connect-github-btn"
                className="bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC]">
                Connect GitHub
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/35 dark:text-white/35" />
                <Input value={repoSearch} onChange={(e) => setRepoSearch(e.target.value)}
                  placeholder="Search repositories…" data-testid="vapt-repo-search"
                  className="pl-9 border-black/15 dark:border-white/15" />
              </div>
              <div className="max-h-80 overflow-y-auto space-y-1.5 mt-1">
                {reposLoading ? (
                  <div className="flex items-center gap-2 text-sm text-black/50 dark:text-white/50 py-8 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading repositories…
                  </div>
                ) : filteredRepos.length === 0 ? (
                  <p className="text-sm text-black/50 dark:text-white/50 py-8 text-center">No repositories found.</p>
                ) : filteredRepos.map((r) => (
                  <div key={r.full_name}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-black/8 dark:border-white/8 hover:bg-black/[0.03] dark:hover:bg-white/[0.05]">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#0A0A0A] dark:text-[#F5F5F5] truncate">{r.full_name}</span>
                        {r.private && <Badge variant="outline" className="text-[9px] px-1.5 border-black/20 dark:border-white/20">private</Badge>}
                      </div>
                      {r.language && <span className="text-[11px] text-black/45 dark:text-white/45">{r.language}</span>}
                    </div>
                    <Button size="sm" disabled={r.already_added} onClick={() => addAsset(r)}
                      data-testid={`vapt-repo-add-${r.full_name}`}
                      variant={r.already_added ? 'outline' : 'default'}
                      className={r.already_added ? 'border-black/15 dark:border-white/15 text-black/45' : 'bg-[#0A0A0A] dark:bg-[#141414] text-white hover:bg-[#0F62FE]'}>
                      {r.already_added ? 'Added' : <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>}
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Finding detail dialog */}
      <Dialog open={!!activeFinding} onOpenChange={(o) => !o && setActiveFinding(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vapt-finding-dialog">
          {activeFinding && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <SevBadge sev={activeFinding.severity} />
                  <Badge variant="outline" className="text-[10px] border-black/15 dark:border-white/15">
                    {(CAT_META[activeFinding.category] || {}).label || activeFinding.category}
                  </Badge>
                  {activeFinding.false_positive_likelihood && (
                    <Badge variant="outline" className="text-[10px] border-black/15 dark:border-white/15">
                      FP likelihood: {activeFinding.false_positive_likelihood}
                    </Badge>
                  )}
                </div>
                <DialogTitle className="text-base leading-snug">{activeFinding.title}</DialogTitle>
                {activeFinding.location?.file && (
                  <DialogDescription className="font-mono text-xs">
                    {activeFinding.location.file}{activeFinding.location.line ? `:${activeFinding.location.line}` : ''}
                  </DialogDescription>
                )}
              </DialogHeader>

              <div className="space-y-4 text-sm">
                {(activeFinding.cve_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {activeFinding.cve_ids.map((cve) => (
                      <Badge key={cve} className="bg-red-100 text-red-700 border-none text-[10px]">{cve}</Badge>
                    ))}
                    {activeFinding.cvss && <Badge variant="outline" className="text-[10px]">CVSS {activeFinding.cvss}</Badge>}
                  </div>
                )}

                {activeFinding.ai_explanation && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Sparkles className="w-3.5 h-3.5 text-[#7C3AED]" />
                      <span className="text-[11px] font-label uppercase tracking-wide text-black/55 dark:text-white/55">AI Explanation</span>
                    </div>
                    <p className="text-black/75 dark:text-white/75 leading-relaxed">{activeFinding.ai_explanation}</p>
                  </div>
                )}

                {activeFinding.description && (
                  <div>
                    <span className="text-[11px] font-label uppercase tracking-wide text-black/55 dark:text-white/55">Details</span>
                    <p className="text-black/75 dark:text-white/75 leading-relaxed whitespace-pre-wrap mt-1">{activeFinding.description}</p>
                  </div>
                )}

                <div>
                  <span className="text-[11px] font-label uppercase tracking-wide text-black/55 dark:text-white/55">Remediation</span>
                  <p className="text-black/75 dark:text-white/75 leading-relaxed mt-1">
                    {activeFinding.ai_remediation || activeFinding.remediation || 'Review and remediate the flagged issue.'}
                  </p>
                </div>

                {(activeFinding.control_mappings || []).length > 0 && (
                  <div>
                    <span className="text-[11px] font-label uppercase tracking-wide text-black/55 dark:text-white/55">Compliance Impact</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {activeFinding.control_mappings.map((m, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] border-black/15 dark:border-white/15">
                          {m.framework} · {m.control}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {activeFinding.reference && (
                  <a href={activeFinding.reference} target="_blank" rel="noreferrer"
                    className="text-[12px] text-[#0F62FE] dark:text-[#5C97FF] inline-flex items-center gap-1 hover:underline">
                    Reference <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActiveFinding(null)}
                  className="border-black/15 dark:border-white/15">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VaptDashboard;
